// SOW Component Configuration
// This module contains all configuration data for the Scope of Work component.
// Configuration is extracted from Stormfield Capital's Excel templates:
// - Scope_of_Work_Template_SFR.xlsm
// - Scope_of_Work_Template_2-9_Units.xlsm
// To modify the form structure (add/remove fields, change categories),
// edit this file. No component code changes are required.

import { PropertyType, isSingleFamilyTemplate } from './sow.types';
import type { SOWConfig, CategoryConfig } from './sow.types';

// ============================================
// CONSTANTS
// ============================================

// ============================================
// CATEGORY DEFINITIONS
// ============================================

// Shared category definitions
const GENERAL: CategoryConfig = {
  id: 'general',
  name: 'General',
  scope: 'building-wide',
  hasDescription: true,
  instructions: 'Describe any general conditions needed for this project (ex: building permit, other permits, plans and permits, architecture and structure engineering, etc.). Leave blank if you\'re not doing work in this category.',
  fields: [
    { id: 'permits_zoning_building_environmental', label: 'Permit(s): Zoning, Building, Environmental' },
    { id: 'impact_fees_engineering_survey', label: 'Impact Fees, Engineering & Survey Fees' },
    { id: 'plans_specifications', label: 'Plans & Specifications' },
    { id: 'dumpster_portajon', label: 'Dumpster/Porta-Jon Costs' },
  ],
};

const DEMOLITION: CategoryConfig = {
  id: 'demolition_site_work',
  name: 'Demolition & Site Work',
  scope: 'building-wide',
  hasDescription: true,
  instructions: 'Describe any demolition & site work needed for this project. Leave blank if you\'re not doing work in this category.',
  fields: [
    { id: 'demolition', label: 'Demolition' },
    { id: 'clearing_site_grading', label: 'Clearing and Site Grading' },
    { id: 'erosion_control', label: 'Erosion Control' },
    { id: 'town_water_tap_hookup', label: 'Town Water: Tap Fee & Hookup' },
    { id: 'town_sewer_tap_hookup', label: 'Town Sewer: Tap Fee & Hookup' },
    { id: 'well', label: 'Well' },
    { id: 'septic', label: 'Septic' },
    { id: 'soil_perc_testing', label: 'Soil & Perc Testing' },
    { id: 'extermination', label: 'Extermination Costs' },
  ],
};

const FOUNDATION_MULTI: CategoryConfig = {
  id: 'foundation',
  name: 'Foundation',
  scope: 'building-wide',
  hasDescription: true,
  instructions: 'Describe the foundation repairs that will occur during this renovation. List all of the repair tasks you are planning to perform (ex: install 4 new concrete piers, level the sunken floor slab, repair damaged stem walls, etc.). Leave blank if you\'re not doing work in this category.',
  fields: [
    { id: 'pre_treat', label: 'Pre-Treat' },
    { id: 'excavation_backfill', label: 'Excavation & Backfill' },
    { id: 'footings', label: 'Footings' },
    { id: 'slab_forms_pour_finish', label: 'Slab (Forms/Pour/Place/Finish/Leveling); Sea Wall' },
    { id: 'waterproofing_vapor_barrier', label: 'Waterproofing/Vapor Barrier' },
    { id: 'exterior_drainage_system', label: 'Water Proofing & Exterior Drainage System' },
  ],
};

const FOUNDATION_SFR: CategoryConfig = {
  ...FOUNDATION_MULTI,
  fields: FOUNDATION_MULTI.fields.filter(f => f.id !== 'pre_treat'),
};

const EXTERIOR: CategoryConfig = {
  id: 'exterior',
  name: 'Exterior',
  scope: 'building-wide',
  hasDescription: true,
  instructions: 'Describe any exterior work needed for this project. Specify for each item if the work is for repair or full replacement. Leave blank if you\'re not doing work in this category.',
  fields: [
    { id: 'roofing_trusses_shingle', label: 'Roofing (Trusses & Shingle) Materials & Installation' },
    { id: 'soffit_fascia', label: 'Soffit/Fascia Materials & Installation' },
    { id: 'weather_barrier', label: 'Weather Barrier Materials & Installation' },
    { id: 'siding', label: 'Siding Materials & Installation' },
    { id: 'painting_exterior', label: 'Painting - Exterior Materials & Installation' },
    { id: 'gutters_downspout', label: 'Gutters & Downspout Materials & Installation' },
    { id: 'driveway_gravel', label: 'Gravel/Rock/Driveway Materials & Installation' },
    { id: 'walkway', label: 'Walkway Materials & Installation' },
    { id: 'landscaping_retaining_walls', label: 'Landscaping & Retaining Walls Materials & Installation' },
    { id: 'decks_patios', label: 'Decks/Patios Materials & Installation' },
    { id: 'exterior_fireplace', label: 'Exterior Fireplace(s) Materials & Installation' },
  ],
};

const LANDSCAPING: CategoryConfig = {
  id: 'landscaping',
  name: 'Landscaping',
  scope: 'building-wide',
  hasDescription: true,
  instructions: 'Describe any site or landscaping improvements that are planned during the renovation (ex: tree removal, install new privacy fence, plant new shrubs/flowers, etc.). Leave blank if you\'re not doing work in this category.',
  fields: [
    { id: 'landscaping_materials_labor', label: 'Materials and Labor' },
  ],
};

const SYSTEM_CONTRACTOR: CategoryConfig = {
  id: 'system_contractor_work',
  name: 'System Contractor Work',
  scope: 'per-unit',
  hasDescription: true,
  instructions: 'Describe any system contractor work needed for this project. Specify for each item if the work is for repair or full replacement. Leave blank if you\'re not doing work in this category.',
  fields: [
    { id: 'plumbing_rough', label: 'Plumbing - Rough' },
    { id: 'plumbing_finish', label: 'Plumbing - Finish' },
    { id: 'electrical_rough', label: 'Electrical - Rough' },
    { id: 'electrical_finish', label: 'Electrical - Finish' },
    { id: 'hvac_rough', label: 'HVAC - Rough' },
    { id: 'hvac_finish', label: 'HVAC - Finish' },
    { id: 'gas_rough', label: 'Gas - Rough' },
    { id: 'gas_finish', label: 'Gas - Finish' },
  ],
};

const STRUCTURE: CategoryConfig = {
  id: 'structure',
  name: 'Structure',
  scope: 'per-unit',
  hasDescription: true,
  instructions: 'Describe the structure work that will occur during this renovation. List all the tasks that will be performed (ex: install LVL beam to open the layout, install new joists & subfloor in the bedroom, install 10 new windows, etc.). Leave blank if you\'re not doing work in this category.',
  fields: [
    { id: 'framing', label: 'Framing Materials & Installation' },
    { id: 'stairs', label: 'Stair Materials & Installation' },
    { id: 'window_materials', label: 'Window Materials' },
    { id: 'window_installation', label: 'Window Installation' },
    { id: 'exterior_door', label: 'Exterior Door Materials & Installation' },
    { id: 'garage_door', label: 'Garage Door Materials & Installation' },
    { id: 'sprinkler_system', label: 'Sprinkler System Materials & Installation' },
    { id: 'alarm_system', label: 'Alarm System Materials & Installation' },
  ],
};

const INTERIOR: CategoryConfig = {
  id: 'interior',
  name: 'Interior',
  scope: 'per-unit',
  hasDescription: true,
  instructions: 'Describe any interior work needed for this project. Specify for each item if the work is for repair or full replacement. Leave blank if you\'re not doing work in this category.',
  fields: [
    { id: 'insulation', label: 'Insulation Materials & Installation' },
    { id: 'drywall', label: 'Drywall Materials & Installation' },
    { id: 'painting_interior', label: 'Painting - Interior Materials & Installation' },
    { id: 'carpentry_trim', label: 'Carpentry (closets, trim & moulding) Materials & Installation' },
    { id: 'interior_door', label: 'Interior Door Materials & Installation' },
    { id: 'lighting_fixtures', label: 'Lighting & Fan Fixture Materials & Installation' },
    { id: 'interior_fireplace', label: 'Interior Fireplace(s) Materials & Installation' },
  ],
};

const KITCHEN: CategoryConfig = {
  id: 'kitchen',
  name: 'Kitchen',
  scope: 'per-unit',
  hasDescription: true,
  instructions: 'Describe the kitchen work that will occur during this renovation. Tell us if you intend to refurbish the cabinets/countertops, or if all-new materials will be installed. If new countertops will be installed, note the material type. Leave blank if you\'re not doing work in this category.',
  fields: [
    { id: 'kitchen_cabinets_materials', label: 'Kitchen Cabinets Materials' },
    { id: 'kitchen_cabinets_installation', label: 'Kitchen Cabinets Installation' },
    { id: 'kitchen_countertops_materials', label: 'Kitchen Countertops Materials' },
    { id: 'kitchen_countertops_installation', label: 'Kitchen Countertops Installation' },
    { id: 'kitchen_appliances_materials', label: 'Kitchen Appliances Materials' },
    { id: 'kitchen_appliances_installation', label: 'Kitchen Appliances Installation' },
    { id: 'kitchen_backsplash', label: 'Kitchen Backsplash Materials & Installation' },
    { id: 'kitchen_other', label: 'Kitchen Other Construction Costs' },
  ],
};

const BATHROOM: CategoryConfig = {
  id: 'bathroom',
  name: 'Bathroom',
  scope: 'per-unit',
  hasDescription: true,
  instructions: 'Describe the work that will occur in each bathroom, including the type of materials that will be installed. Rough plumbing work should be captured separately in System Contractor Work. Leave blank if you\'re not doing work in this category.',
  fields: [
    { id: 'bathroom_materials_labor', label: 'Bathroom Materials & Labor (Toilets, Vanities, Countertops, Sinks, Hardware, Shower/Tub, Tiles)' },
    { id: 'bathroom_other', label: 'Bathroom Other Construction Costs' },
  ],
};

const FLOORING: CategoryConfig = {
  id: 'flooring',
  name: 'Flooring',
  scope: 'per-unit',
  hasDescription: true,
  instructions: 'Describe the flooring work that will occur during this renovation. Specify the type of material(s) used along with the location(s) of the new flooring. Please state if any existing floors will be refinished. Leave blank if you\'re not doing work in this category.',
  fields: [
    { id: 'flooring_materials_installation', label: 'Flooring Materials and Installation (excluding bathrooms)' },
  ],
};

const MISCELLANEOUS: CategoryConfig = {
  id: 'miscellaneous',
  name: 'Miscellaneous',
  scope: 'per-unit',
  hasDescription: true,
  instructions: 'This line can be used to capture work that doesn\'t fit into other line items. If multiple tasks are listed here, please note itemized budgets within the description for each task.',
  fields: [
    { id: 'miscellaneous', label: 'Miscellaneous' },
  ],
};

// Ordered category arrays matching Excel template order
const MULTI_UNIT_CATEGORIES: readonly CategoryConfig[] = [
  GENERAL,
  DEMOLITION,
  SYSTEM_CONTRACTOR,
  FOUNDATION_MULTI,
  STRUCTURE,
  EXTERIOR,
  INTERIOR,
  KITCHEN,
  BATHROOM,
  FLOORING,
  LANDSCAPING,
  MISCELLANEOUS,
];

const SINGLE_UNIT_CATEGORIES: readonly CategoryConfig[] = [
  GENERAL,
  DEMOLITION,
  SYSTEM_CONTRACTOR,
  FOUNDATION_SFR,
  STRUCTURE,
  EXTERIOR,
  INTERIOR,
  KITCHEN,
  BATHROOM,
  FLOORING,
  LANDSCAPING,
  MISCELLANEOUS,
];

// All categories (defaults to multi-unit order for backward compatibility)
const CATEGORIES: readonly CategoryConfig[] = MULTI_UNIT_CATEGORIES;

// ============================================
// MAIN CONFIG EXPORT
// ============================================

// Complete SOW configuration
export const sowConfig: SOWConfig = {
  maxUnits: 30,
  minUnits: 2,
  maxTotalAmount: 5000000, // 5 million dollar limit matching loan system
  defaultPropertyType: PropertyType.SFR,
  contingencyPercentage: parseFloat(process.env.SOW_CONTINGENCY_PERCENTAGE || '0.05'),
  contingencyMinimum: parseInt(process.env.SOW_CONTINGENCY_MINIMUM || '1000', 10),
  categories: CATEGORIES,
};

// ============================================
// CONFIG ACCESSOR FUNCTIONS
// ============================================

// Get categories in the correct order for a given property type
// Order matches the Excel templates: SFR/Condo/Townhouse vs 2-30 Units
export function getCategoriesForPropertyType(propertyType: PropertyType): readonly CategoryConfig[] {
  return isSingleFamilyTemplate(propertyType) ? SINGLE_UNIT_CATEGORIES : MULTI_UNIT_CATEGORIES;
}

// Get a single category by ID
export function getCategoryById(categoryId: string, propertyType: PropertyType = PropertyType.MultiUnit): CategoryConfig | undefined {
  const categories = getCategoriesForPropertyType(propertyType);
  return categories.find(cat => cat.id === categoryId);
}

// Check if a category ID is valid
export function isValidCategoryId(categoryId: string, propertyType: PropertyType = PropertyType.MultiUnit): boolean {
  const categories = getCategoriesForPropertyType(propertyType);
  return categories.some(cat => cat.id === categoryId);
}

// Check if a field ID is valid within a category
export function isValidFieldId(categoryId: string, fieldId: string, propertyType: PropertyType = PropertyType.MultiUnit): boolean {
  const category = getCategoryById(categoryId, propertyType);
  return category ? category.fields.some(f => f.id === fieldId) : false;
}
